// pages/Payments.js - Complete Payment Form

import { useState, useEffect } from "react";
import { database } from "../firebase/firebase";
import { ref, push, onValue, get, update, set } from "firebase/database";
import { useNavigate, useParams } from "react-router-dom";
import "../styles/main.css";

export default function Payments() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  // Payment Type: 'received' (from customers) or 'paid' (to suppliers)
  const [paymentType, setPaymentType] = useState("received");
  
  // State for party selection (customer/supplier)
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredParties, setFilteredParties] = useState([]);
  
  // State for bank account selection (for payments)
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [bankSearchTerm, setBankSearchTerm] = useState("");
  const [filteredBankAccounts, setFilteredBankAccounts] = useState([]);
  
  // Payment details
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [paymentMode, setPaymentMode] = useState("cash");
  const [referenceNo, setReferenceNo] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentNo, setPaymentNo] = useState("");
  const [manualPaymentNo, setManualPaymentNo] = useState("");
  const [paymentNumber, setPaymentNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Financial year
  const [financialYear, setFinancialYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [settings, setSettings] = useState({ financialYear: "2025-26" });
  
  // Company details
  const [companyDetails, setCompanyDetails] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    gstin: ""
  });
  
  // Existing payment numbers
  const [existingPaymentNumbers, setExistingPaymentNumbers] = useState(new Set());
  const [paymentNumberError, setPaymentNumberError] = useState("");
  
  // Clean data function to remove undefined values
  const cleanData = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => cleanData(item));
    } else if (obj && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        const value = obj[key];
        if (value !== undefined && value !== null) {
          if (typeof value === 'object' && !Array.isArray(value)) {
            const cleaned = cleanData(value);
            if (Object.keys(cleaned).length > 0) {
              acc[key] = cleaned;
            }
          } else if (Array.isArray(value)) {
            const cleaned = cleanData(value);
            if (cleaned.length > 0) {
              acc[key] = cleaned;
            }
          } else {
            acc[key] = value;
          }
        }
        return acc;
      }, {});
    }
    return obj;
  };
  
  // Load settings and financial years
  useEffect(() => {
    const settingsRef = ref(database, "settings");
    onValue(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setSettings(prev => ({
          ...prev,
          financialYear: data.financialYear || "2025-26",
          paymentPrefix: data.paymentPrefix || "PAY-",
          allowManualPaymentNumber: data.allowManualPaymentNumber !== false
        }));
        setFinancialYear(data.financialYear || "2025-26");
      }
    });
  }, []);

  // Load company details
  useEffect(() => {
    const companyRef = ref(database, "company");
    onValue(companyRef, (snapshot) => {
      if (snapshot.exists()) {
        setCompanyDetails(snapshot.val());
      }
    });
  }, []);

  // Get available financial years
  useEffect(() => {
    const fetchFinancialYears = async () => {
      const financialYearsRef = ref(database, "financialYears");
      const snapshot = await get(financialYearsRef);
      
      if (snapshot.exists()) {
        const years = Object.keys(snapshot.val());
        setAvailableYears(years.sort().reverse());
      } else {
        setAvailableYears([settings.financialYear]);
      }
    };
    
    fetchFinancialYears();
  }, [settings.financialYear]);

  // Load bank accounts from masters (type: bankAccount)
  useEffect(() => {
    const customersRef = ref(database, "customers");
    onValue(customersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const bankAccountList = [];
        
        Object.entries(data).forEach(([id, account]) => {
          if (account.masterType === "bankAccount") {
            bankAccountList.push({
              id,
              name: account.name || "",
              bankName: account.bankName || "",
              accountNumber: account.accountNumber || "",
              accountHolderName: account.accountHolderName || "",
              ifscCode: account.ifscCode || "",
              mobile: account.mobile || "",
              email: account.email || "",
              address: account.address || "",
              balance: account.balance || 0,
              masterType: "bankAccount",
              masterTypeDisplay: "Bank Account"
            });
          }
        });
        
        setBankAccounts(bankAccountList);
      }
    });
  }, []);

  // Filter bank accounts
  useEffect(() => {
    if (bankSearchTerm.trim() === "") {
      setFilteredBankAccounts([]);
      return;
    }
    
    const filtered = bankAccounts.filter(account => 
      account.name?.toLowerCase().includes(bankSearchTerm.toLowerCase()) ||
      account.bankName?.toLowerCase().includes(bankSearchTerm.toLowerCase()) ||
      account.accountNumber?.includes(bankSearchTerm)
    );
    setFilteredBankAccounts(filtered);
  }, [bankSearchTerm, bankAccounts]);

  // Load parties based on payment type
  useEffect(() => {
    const customersRef = ref(database, "customers");
    onValue(customersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        let partyList = [];
        
        Object.entries(data).forEach(([id, customer]) => {
          // Skip bank accounts when loading parties
          if (customer.masterType === "bankAccount") return;
          
          const partyData = {
            id,
            name: customer.name || "",
            mobile: customer.mobile || "",
            gstin: customer.gstin || customer.gst || "",
            address: customer.address || "",
            state: customer.state || "",
            masterType: customer.masterType || "sundryDebtor",
            openingBalance: customer.openingBalance || 0,
            balance: customer.balance || 0
          };
          
          // For received payments: show all customers (sundry debtors)
          // For paid payments: show only suppliers (sundry creditors)
          if (paymentType === "received") {
            if (partyData.masterType !== "sundryCreditor") {
              partyList.push(partyData);
            }
          } else {
            if (partyData.masterType === "sundryCreditor") {
              partyList.push(partyData);
            }
          }
        });
        
        setParties(partyList);
      }
    });
  }, [paymentType]);

  // Filter parties based on search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredParties([]);
      return;
    }
    
    const filtered = parties.filter(party => 
      party.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      party.mobile?.includes(searchTerm)
    );
    setFilteredParties(filtered);
  }, [searchTerm, parties]);

  // Load existing payment numbers
  useEffect(() => {
    if (financialYear) {
      loadExistingPaymentNumbers();
    }
  }, [financialYear]);

  // Generate payment number
  useEffect(() => {
    if (financialYear && !isEditMode && settings.allowManualPaymentNumber !== false) {
      generatePaymentNumber();
    }
  }, [financialYear, paymentType]);

  const loadExistingPaymentNumbers = async () => {
    try {
      const paymentsRef = ref(database, `financialYears/${financialYear}/payments`);
      const snapshot = await get(paymentsRef);
      const numbers = new Set();
      let lastNum = 0;
      
      if (snapshot.exists()) {
        const payments = snapshot.val();
        Object.values(payments).forEach(payment => {
          if (payment.paymentNo) {
            numbers.add(payment.paymentNo);
            const match = payment.paymentNo.match(/\d+/);
            if (match) {
              const num = parseInt(match[0], 10);
              if (!isNaN(num) && num > lastNum) lastNum = num;
            }
          }
        });
      }
      
      setExistingPaymentNumbers(numbers);
      return { numbers, lastNumber: lastNum };
    } catch (error) {
      console.error("Error loading payment numbers:", error);
      return { numbers: new Set(), lastNumber: 0 };
    }
  };

  const generatePaymentNumber = async () => {
    try {
      const prefix = settings.paymentPrefix || 'PAY-';
      const { lastNumber } = await loadExistingPaymentNumbers();
      
      let newNumber;
      if (lastNumber > 0) {
        newNumber = lastNumber + 1;
      } else {
        newNumber = 1;
      }
      
      setPaymentNumber(newNumber);
      const newPaymentNo = `${prefix}${newNumber}`;
      setPaymentNo(newPaymentNo);
      setManualPaymentNo(newPaymentNo);
      setPaymentNumberError("");
      
      return newNumber;
    } catch (error) {
      console.error("Error generating payment number:", error);
      const fallbackNumber = 1;
      setPaymentNumber(fallbackNumber);
      setPaymentNo(`${settings.paymentPrefix || 'PAY-'}${fallbackNumber}`);
      return fallbackNumber;
    }
  };

  const isPaymentNumberDuplicate = (number) => {
    if (isEditMode) {
      return existingPaymentNumbers.has(number) && number !== paymentNo;
    }
    return existingPaymentNumbers.has(number);
  };

  const handleManualPaymentNoChange = (e) => {
    const value = e.target.value;
    setManualPaymentNo(value);
    setPaymentNo(value);
    
    const match = value.match(/\d+/);
    if (match) {
      setPaymentNumber(parseInt(match[0], 10));
    }
    
    if (isPaymentNumberDuplicate(value)) {
      setPaymentNumberError("This payment number already exists. Please use a different number.");
    } else {
      setPaymentNumberError("");
    }
  };

  const selectParty = (party) => {
    setSelectedParty(party);
    setSearchTerm(party.name);
    setShowDropdown(false);
  };

  const selectBankAccount = (account) => {
    setSelectedBankAccount(account);
    setBankSearchTerm(account.name);
    setShowBankDropdown(false);
  };

  const paymentModes = [
    { value: "cash", label: "Cash", icon: "💵" },
    { value: "bank", label: "Bank Transfer", icon: "🏦" },
    { value: "cheque", label: "Cheque", icon: "📝" },
    { value: "upi", label: "UPI", icon: "📱" },
    { value: "card", label: "Card Payment", icon: "💳" }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const handleSavePayment = async () => {
    if (!selectedParty) {
      alert(`Please select a ${paymentType === "received" ? "customer" : "supplier"}`);
      return;
    }
    
    if (!selectedBankAccount) {
      alert(`Please select a bank account to ${paymentType === "received" ? "receive the payment" : "make the payment from"}`);
      return;
    }
    
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert("Please enter a valid payment amount");
      return;
    }
    
    if (paymentNumberError) {
      alert("Please fix the payment number error");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const timestamp = new Date().toISOString();
      const amount = parseFloat(paymentAmount);
      const fy = financialYear;
      
      // Build payment data
      const paymentData = {
        paymentNo: paymentNo || "",
        paymentType: paymentType,
        paymentDate: paymentDate,
        amount: amount,
        paymentMode: paymentMode,
        financialYear: fy,
        partyId: selectedParty.id,
        partyName: selectedParty.name,
      };
      
      // Add optional fields
      if (selectedParty.mobile) paymentData.partyMobile = selectedParty.mobile;
      if (selectedParty.gstin) paymentData.partyGST = selectedParty.gstin;
      if (selectedParty.address) paymentData.partyAddress = selectedParty.address;
      if (referenceNo) paymentData.referenceNo = referenceNo;
      if (notes) paymentData.notes = notes;
      
      // Add bank account details
      if (selectedBankAccount) {
        paymentData.bankAccountId = selectedBankAccount.id;
        paymentData.bankAccountName = selectedBankAccount.name;
        if (selectedBankAccount.bankName) paymentData.bankName = selectedBankAccount.bankName;
        if (selectedBankAccount.accountNumber) paymentData.accountNumber = selectedBankAccount.accountNumber;
        if (selectedBankAccount.accountHolderName) paymentData.accountHolderName = selectedBankAccount.accountHolderName;
        if (selectedBankAccount.ifscCode) paymentData.ifscCode = selectedBankAccount.ifscCode;
      }
      
      // Add company details
      const companyInfo = {};
      if (companyDetails?.name) companyInfo.name = companyDetails.name;
      if (companyDetails?.address) companyInfo.address = companyDetails.address;
      if (companyDetails?.phone) companyInfo.phone = companyDetails.phone;
      if (companyDetails?.gstin) companyInfo.gstin = companyDetails.gstin;
      
      if (Object.keys(companyInfo).length > 0) {
        paymentData.companyDetails = companyInfo;
      }
      
      // Add timestamps
      if (isEditMode) {
        paymentData.updatedAt = timestamp;
      } else {
        paymentData.createdAt = timestamp;
        paymentData.updatedAt = timestamp;
      }
      
      paymentData.status = "completed";
      
      // Clean the data
      const cleanedPaymentData = cleanData(paymentData);
      
      console.log("Saving payment data:", cleanedPaymentData);
      
      // Save to financial years structure
      const paymentsRef = ref(database, `financialYears/${fy}/payments`);
      
      if (isEditMode) {
        const snapshot = await get(paymentsRef);
        if (snapshot.exists()) {
          const payments = snapshot.val();
          let paymentKey = null;
          
          for (const [key, payment] of Object.entries(payments)) {
            if (payment.paymentNo === paymentNo || key === id) {
              paymentKey = key;
              break;
            }
          }
          
          if (paymentKey) {
            await update(ref(database, `financialYears/${fy}/payments/${paymentKey}`), cleanedPaymentData);
          }
        }
      } else {
        // Create new payment
        await push(paymentsRef, cleanedPaymentData);
        
        // Update party's current balance (NOT opening balance)
        const partyRef = ref(database, `customers/${selectedParty.id}`);
        const partySnapshot = await get(partyRef);
        
        if (partySnapshot.exists()) {
          const party = partySnapshot.val();
          let currentBalance = party.balance || 0;
          let newBalance;
          
          if (paymentType === "received") {
            // Payment received from customer - REDUCES the balance
            newBalance = currentBalance - amount;
          } else {
            // Payment paid to supplier - INCREASES the balance
            newBalance = currentBalance + amount;
          }
          
          await update(partyRef, {
            balance: newBalance,
            lastPaymentDate: paymentDate,
            lastPaymentAmount: amount,
            updatedAt: timestamp
          });
          
          console.log(`Updated ${selectedParty.name} balance: ${currentBalance} -> ${newBalance}`);
        }
        
        // Update bank account balance
        if (selectedBankAccount) {
          const bankRef = ref(database, `customers/${selectedBankAccount.id}`);
          const bankSnapshot = await get(bankRef);
          if (bankSnapshot.exists()) {
            const bankAccount = bankSnapshot.val();
            let newBankBalance;
            
            if (paymentType === "received") {
              newBankBalance = (bankAccount.balance || 0) + amount;
            } else {
              newBankBalance = (bankAccount.balance || 0) - amount;
            }
            
            await update(bankRef, {
              balance: newBankBalance,
              lastTransactionDate: paymentDate,
              lastTransactionAmount: amount,
              lastTransactionType: paymentType === "received" ? "credit" : "debit",
              updatedAt: timestamp
            });
            
            console.log(`Updated bank ${selectedBankAccount.name} balance: ${bankAccount.balance || 0} -> ${newBankBalance}`);
          }
        }
        
        await generatePaymentNumber();
      }
      
      alert(`Payment ${paymentNo} saved successfully!`);
      
      if (!isEditMode) {
        setSelectedParty(null);
        setSearchTerm("");
        setSelectedBankAccount(null);
        setBankSearchTerm("");
        setPaymentAmount("");
        setPaymentMode("cash");
        setReferenceNo("");
        setNotes("");
      } else {
        setTimeout(() => {
          navigate("/payment-list");
        }, 1500);
      }
      
    } catch (error) {
      console.error("Error saving payment:", error);
      setError(error.message || "Failed to save payment");
      alert(`Failed to save payment: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedParty(null);
    setSearchTerm("");
    setSelectedBankAccount(null);
    setBankSearchTerm("");
    setPaymentAmount("");
    setPaymentMode("cash");
    setReferenceNo("");
    setNotes("");
    setPaymentNumberError("");
    generatePaymentNumber();
  };

  return (
    <div className="payments-page">
      {/* Header */}
      <div className="page-header">
        <h2>💸 {paymentType === "received" ? "Payment Received" : "Payment Paid"}</h2>
        <div className="header-actions">
          <button onClick={() => navigate("/")} className="back-btn">
            🏠 Home
          </button>
          <button onClick={() => navigate("/payments")} className="list-btn">
            📋 List
          </button>
        </div>
      </div>
      
      {/* Payment Type Toggle */}
      <div className="payment-type-toggle">
        <button 
          className={`toggle-btn ${paymentType === "received" ? "active" : ""}`}
          onClick={() => {
            setPaymentType("received");
            setSelectedParty(null);
            setSearchTerm("");
            setSelectedBankAccount(null);
            setBankSearchTerm("");
            generatePaymentNumber();
          }}
        >
          💰 Payment Received
        </button>
        <button 
          className={`toggle-btn ${paymentType === "paid" ? "active" : ""}`}
          onClick={() => {
            setPaymentType("paid");
            setSelectedParty(null);
            setSearchTerm("");
            setSelectedBankAccount(null);
            setBankSearchTerm("");
            generatePaymentNumber();
          }}
        >
          💸 Payment Paid
        </button>
      </div>
      
      {/* Financial Year Selector */}
      <div className="financial-year-selector">
        <label>Financial Year:</label>
        <select 
          value={financialYear} 
          onChange={(e) => setFinancialYear(e.target.value)}
          className="year-select"
        >
          {availableYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>
      
      {/* Error Alert */}
      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}
      
      <div className="payments-container">
        {/* Left Column */}
        <div className="form-section">
          {/* Payment Number Card */}
          <div className="card">
            <h3>Payment Voucher No.</h3>
            {settings.allowManualPaymentNumber !== false ? (
              <div>
                <div className="input-group">
                  <input
                    type="text"
                    value={manualPaymentNo}
                    onChange={handleManualPaymentNoChange}
                    placeholder="Enter payment number"
                    className={paymentNumberError ? 'error' : ''}
                  />
                </div>
                {paymentNumberError && <p className="error-message">{paymentNumberError}</p>}
                <p className="hint">Payment numbers are unique per financial year</p>
              </div>
            ) : (
              <div>
                <div className="invoice-display">
                  <span>{settings.paymentPrefix || 'PAY-'}</span>
                  <input
                    type="number"
                    value={paymentNumber}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setPaymentNumber(val);
                      setPaymentNo(`${settings.paymentPrefix || 'PAY-'}${val}`);
                    }}
                    min="1"
                  />
                </div>
                <p className="hint">Next payment number: {paymentNumber}</p>
              </div>
            )}
          </div>
          
          {/* Date Card */}
          <div className="card">
            <h3>Payment Date</h3>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="date-input"
            />
          </div>
          
          {/* Party Search Card */}
          <div className="card">
            <h3>{paymentType === "received" ? "Customer Details" : "Supplier Details"}</h3>
            <div className="party-selector">
              <div className="search-container">
                <input
                  type="text"
                  className="party-search-input"
                  placeholder={`Search ${paymentType === "received" ? "customer" : "supplier"} by name or mobile...`}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                    if (!e.target.value) {
                      setSelectedParty(null);
                    }
                  }}
                  onFocus={() => setShowDropdown(true)}
                />
                
                {showDropdown && filteredParties.length > 0 && (
                  <div className="party-dropdown">
                    {filteredParties.map(party => (
                      <div 
                        key={party.id} 
                        className="party-option"
                        onClick={() => selectParty(party)}
                      >
                        <div className="party-name">{party.name}</div>
                        <div className="party-detail">
                          {party.mobile && <span>📱 {party.mobile}</span>}
                          {party.gstin && <span> | GST: {party.gstin}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {selectedParty && (
                <div className="selected-party">
                  <div className="party-header">
                    <strong>{selectedParty.name}</strong>
                    <button className="change-btn" onClick={() => {
                      setSelectedParty(null);
                      setSearchTerm("");
                    }}>
                      Change
                    </button>
                  </div>
                  {selectedParty.mobile && <div>📱 {selectedParty.mobile}</div>}
                  {selectedParty.gstin && <div>🆔 GST: {selectedParty.gstin}</div>}
                  {selectedParty.address && <div>📍 {selectedParty.address}</div>}
                </div>
              )}
            </div>
          </div>

          {/* Bank Account Selection */}
          <div className="card">
            <h3>
              {paymentType === "received" ? "💰 Receive Payment To" : "💸 Make Payment From"}
            </h3>
            <div className="bank-selector">
              <div className="search-container">
                <input
                  type="text"
                  className="bank-search-input"
                  placeholder={`Select bank account...`}
                  value={bankSearchTerm}
                  onChange={(e) => {
                    setBankSearchTerm(e.target.value);
                    setShowBankDropdown(true);
                    if (!e.target.value) {
                      setSelectedBankAccount(null);
                    }
                  }}
                  onFocus={() => setShowBankDropdown(true)}
                />
                
                {showBankDropdown && filteredBankAccounts.length > 0 && (
                  <div className="bank-dropdown">
                    {filteredBankAccounts.map(account => (
                      <div 
                        key={account.id} 
                        className="bank-option"
                        onClick={() => selectBankAccount(account)}
                      >
                        <div className="bank-name">
                          <strong>{account.name}</strong>
                        </div>
                        <div className="bank-detail">
                          {account.bankName && <span>🏦 {account.bankName}</span>}
                          {account.accountNumber && <span> | Acc: {account.accountNumber.slice(-4)}</span>}
                        </div>
                        <div className="bank-balance">
                          Balance: {formatCurrency(account.balance)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {selectedBankAccount && (
                <div className="selected-bank">
                  <div className="bank-header">
                    <strong>{selectedBankAccount.name}</strong>
                    <button className="change-btn" onClick={() => {
                      setSelectedBankAccount(null);
                      setBankSearchTerm("");
                    }}>
                      Change
                    </button>
                  </div>
                  <div className="bank-details">
                    {selectedBankAccount.bankName && <div>🏦 {selectedBankAccount.bankName}</div>}
                    {selectedBankAccount.accountNumber && (
                      <div>🔢 Account: {selectedBankAccount.accountNumber}</div>
                    )}
                    {selectedBankAccount.accountHolderName && (
                      <div>👤 Holder: {selectedBankAccount.accountHolderName}</div>
                    )}
                    {selectedBankAccount.ifscCode && (
                      <div>🏧 IFSC: {selectedBankAccount.ifscCode}</div>
                    )}
                    <div className="bank-current-balance">
                      💰 Current Balance: {formatCurrency(selectedBankAccount.balance)}
                    </div>
                    <div className="transaction-direction">
                      {paymentType === "received" ? (
                        <span className="credit-text">↓ Amount will be CREDITED to this account</span>
                      ) : (
                        <span className="debit-text">↑ Amount will be DEBITED from this account</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {!selectedBankAccount && (
                <div className="bank-empty-state">
                  <p>No bank account selected</p>
                  <small>
                    {paymentType === "received" 
                      ? "Select a bank account where this payment will be received" 
                      : "Select a bank account from which this payment will be made"}
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Right Column */}
        <div className="form-section">
          {/* Payment Amount Card */}
          <div className="card">
            <h3>Payment Amount</h3>
            <div className="amount-input-container">
              <span className="currency-symbol">₹</span>
              <input
                type="number"
                className="amount-input"
                placeholder="0.00"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                step="0.01"
                min="0"
              />
            </div>
            {selectedParty && (
              <div className="balance-info">
                <small>
                  {paymentType === "received" ? "Current Receivable" : "Current Payable"}: 
                  <strong> {formatCurrency(Math.abs(selectedParty.balance || 0))}</strong>
                </small>
              </div>
            )}
          </div>
          
          {/* Payment Mode Card */}
          <div className="card">
            <h3>Payment Mode</h3>
            <div className="payment-modes-grid">
              {paymentModes.map(mode => (
                <button
                  key={mode.value}
                  className={`mode-btn ${paymentMode === mode.value ? "active" : ""}`}
                  onClick={() => setPaymentMode(mode.value)}
                >
                  <span className="mode-icon">{mode.icon}</span>
                  <span className="mode-label">{mode.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Reference Number Card */}
          {(paymentMode === "bank" || paymentMode === "cheque" || paymentMode === "upi") && (
            <div className="card">
              <h3>
                {paymentMode === "bank" && "Transaction Reference"}
                {paymentMode === "cheque" && "Cheque Details"}
                {paymentMode === "upi" && "UPI Transaction ID"}
              </h3>
              <input
                type="text"
                className="reference-input"
                placeholder={
                  paymentMode === "bank" ? "Transaction ID / Reference No." :
                  paymentMode === "cheque" ? "Cheque No. / Date" :
                  "UPI Transaction ID"
                }
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
              />
            </div>
          )}
          
          {/* Notes Card */}
          <div className="card">
            <h3>Notes (Optional)</h3>
            <textarea
              className="notes-input"
              placeholder="Add any additional notes here..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
            />
          </div>
          
          {/* Action Buttons */}
          <div className="action-buttons">
            <button 
              className="save-btn"
              onClick={handleSavePayment}
              disabled={
                loading || 
                !selectedParty || 
                !selectedBankAccount ||
                !paymentAmount
              }
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Saving...
                </>
              ) : (
                isEditMode ? "💾 Update Payment" : "💾 Save Payment"
              )}
            </button>
            
            <button className="reset-btn" onClick={resetForm} disabled={loading}>
              🆕 New
            </button>
          </div>
        </div>
      </div>
      
      {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>
      
      <style>{`
        .payments-page {
          min-height: 100vh;
          background: #f5f5f5;
          padding-bottom: 70px;
        }
        
        /* Header */
        .page-header {
          background: linear-gradient(135deg, #5a60ea, #6f5bff);
          color: white;
          padding: 20px;
          position: sticky;
          top: 0;
          z-index: 100;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        
        .page-header h2 {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 600;
        }
        
        .header-actions {
          display: flex;
          gap: 10px;
        }
        
        .back-btn, .list-btn {
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }
        
        .back-btn {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        
        .list-btn {
          background: white;
          color: #5a60ea;
        }
        
        .back-btn:hover, .list-btn:hover {
          transform: translateY(-2px);
        }
        
        /* Payment Type Toggle */
        .payment-type-toggle {
          display: flex;
          margin: 20px;
          gap: 10px;
          background: white;
          padding: 5px;
          border-radius: 50px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .toggle-btn {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 40px;
          font-size: 0.85rem;
          font-weight: bold;
          cursor: pointer;
          background: transparent;
          color: #666;
          transition: all 0.3s;
        }
        
        .toggle-btn.active {
          background: linear-gradient(135deg, #5a60ea, #6f5bff);
          color: white;
          box-shadow: 0 2px 8px rgba(90,96,234,0.3);
        }
        
        /* Financial Year Selector */
        .financial-year-selector {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 12px 20px;
          margin: 0 20px 20px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        
        .year-select {
          padding: 8px 16px;
          border-radius: 8px;
          border: none;
          background: white;
          font-weight: bold;
          cursor: pointer;
          font-size: 0.85rem;
        }
        
        /* Error Alert */
        .error-alert {
          background: #ffebee;
          color: #c62828;
          padding: 12px 16px;
          margin: 0 20px 20px;
          border-radius: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.85rem;
        }
        
        .error-alert button {
          background: none;
          border: none;
          font-size: 1.2rem;
          cursor: pointer;
          color: #c62828;
        }
        
        /* Payments Container */
        .payments-container {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
          padding: 0 20px;
        }
        
        @media (min-width: 768px) {
          .payments-container {
            grid-template-columns: 1fr 1fr;
            gap: 20px;
          }
        }
        
        .form-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        /* Cards */
        .card {
          background: white;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .card h3 {
          margin: 0 0 15px 0;
          font-size: 0.95rem;
          color: #333;
          font-weight: 600;
        }
        
        /* Input Styles */
        .input-group {
          display: flex;
          gap: 10px;
        }
        
        .input-group input {
          flex: 1;
          padding: 12px;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          font-size: 0.9rem;
          transition: all 0.2s;
        }
        
        .input-group input:focus {
          outline: none;
          border-color: #5a60ea;
          box-shadow: 0 0 0 2px rgba(90,96,234,0.1);
        }
        
        .input-group input.error {
          border-color: #ff4444;
        }
        
        .error-message {
          color: #ff4444;
          font-size: 0.75rem;
          margin-top: 5px;
        }
        
        .hint {
          font-size: 0.7rem;
          color: #999;
          margin-top: 8px;
        }
        
        .invoice-display {
          display: flex;
          align-items: center;
          background: #f5f5f5;
          border-radius: 12px;
          overflow: hidden;
        }
        
        .invoice-display span {
          padding: 12px 16px;
          background: #e8e8e8;
          font-weight: bold;
          font-size: 0.9rem;
        }
        
        .invoice-display input {
          flex: 1;
          padding: 12px;
          border: none;
          background: #f5f5f5;
          font-size: 0.9rem;
          outline: none;
        }
        
        .date-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          font-size: 0.9rem;
        }
        
        .date-input:focus {
          outline: none;
          border-color: #5a60ea;
          box-shadow: 0 0 0 2px rgba(90,96,234,0.1);
        }
        
        /* Search Containers */
        .search-container {
          position: relative;
        }
        
        .party-search-input, .bank-search-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          font-size: 0.9rem;
        }
        
        .party-search-input:focus, .bank-search-input:focus {
          outline: none;
          border-color: #5a60ea;
          box-shadow: 0 0 0 2px rgba(90,96,234,0.1);
        }
        
        /* Dropdowns */
        .party-dropdown, .bank-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 300px;
          overflow-y: auto;
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          margin-top: 8px;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .party-option, .bank-option {
          padding: 12px 16px;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .party-option:hover, .bank-option:hover {
          background: #f8f9ff;
        }
        
        .party-name, .bank-name {
          font-weight: 600;
          font-size: 0.85rem;
          margin-bottom: 4px;
        }
        
        .party-detail, .bank-detail {
          font-size: 0.7rem;
          color: #666;
        }
        
        .bank-balance {
          font-size: 0.7rem;
          color: #28a745;
          margin-top: 4px;
          font-weight: 500;
        }
        
        /* Selected Items */
        .selected-party, .selected-bank {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          margin-top: 12px;
          border: 1px solid #e8e8e8;
        }
        
        .party-header, .bank-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        
        .party-header strong, .bank-header strong {
          font-size: 0.9rem;
          color: #333;
        }
        
        .selected-party > div, .bank-details > div {
          font-size: 0.8rem;
          color: #666;
          margin-bottom: 6px;
        }
        
        .bank-details {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e8e8e8;
        }
        
        .bank-current-balance {
          margin-top: 12px;
          padding: 10px;
          background: #e8f5e9;
          border-radius: 8px;
          color: #28a745;
          font-weight: 600;
          font-size: 0.85rem;
        }
        
        .transaction-direction {
          margin-top: 12px;
        }
        
        .credit-text, .debit-text {
          display: block;
          padding: 8px;
          border-radius: 8px;
          text-align: center;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .credit-text {
          background: #e8f5e9;
          color: #28a745;
        }
        
        .debit-text {
          background: #ffebee;
          color: #dc3545;
        }
        
        .bank-empty-state {
          text-align: center;
          padding: 24px;
          background: #f9f9f9;
          border-radius: 12px;
          margin-top: 12px;
          color: #999;
        }
        
        .bank-empty-state p {
          margin: 0 0 8px;
          font-size: 0.85rem;
        }
        
        .bank-empty-state small {
          font-size: 0.7rem;
          color: #bbb;
        }
        
        .change-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 0.7rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .change-btn:hover {
          background: #4a50d8;
          transform: translateY(-1px);
        }
        
        /* Amount Input */
        .amount-input-container {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #f8f9fa;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          padding: 5px 16px;
        }
        
        .currency-symbol {
          font-size: 1.5rem;
          font-weight: bold;
          color: #5a60ea;
        }
        
        .amount-input {
          flex: 1;
          padding: 12px 0;
          border: none;
          background: transparent;
          font-size: 1.5rem;
          font-weight: bold;
          outline: none;
        }
        
        .balance-info {
          margin-top: 12px;
          padding: 10px;
          background: #f5f5f5;
          border-radius: 8px;
          text-align: center;
          font-size: 0.8rem;
        }
        
        /* Payment Modes */
        .payment-modes-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }
        
        @media (max-width: 480px) {
          .payment-modes-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        .mode-btn {
          padding: 12px;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        
        .mode-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .mode-btn.active {
          background: linear-gradient(135deg, #5a60ea, #6f5bff);
          color: white;
          border-color: #5a60ea;
        }
        
        .mode-icon {
          font-size: 1.5rem;
        }
        
        .mode-label {
          font-size: 0.7rem;
          font-weight: 500;
        }
        
        /* Reference & Notes */
        .reference-input, .notes-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          font-size: 0.9rem;
        }
        
        .reference-input:focus, .notes-input:focus {
          outline: none;
          border-color: #5a60ea;
          box-shadow: 0 0 0 2px rgba(90,96,234,0.1);
        }
        
        .notes-input {
          resize: vertical;
          font-family: inherit;
        }
        
        /* Action Buttons */
        .action-buttons {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }
        
        .save-btn {
          flex: 2;
          padding: 14px;
          background: linear-gradient(135deg, #5a60ea, #6f5bff);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 0.9rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .save-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(90,96,234,0.3);
        }
        
        .save-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .reset-btn {
          flex: 1;
          padding: 14px;
          background: #f0f0f0;
          color: #666;
          border: none;
          border-radius: 12px;
          font-size: 0.9rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .reset-btn:hover:not(:disabled) {
          background: #e0e0e0;
          transform: translateY(-1px);
        }
        
        .spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid #fff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* Bottom Navigation */
        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }
        
        .nav-item {
          text-align: center;
          cursor: pointer;
          color: #999;
          font-size: 1.2rem;
          transition: color 0.2s;
          padding: 5px 0;
        }
        
        .nav-item.active {
          color: #5a60ea;
        }
        
        .nav-item p {
          margin: 2px 0 0;
          font-size: 0.65rem;
        }
        
        /* Mobile Responsive */
        @media (max-width: 480px) {
          .page-header h2 {
            font-size: 1.1rem;
          }
          
          .back-btn, .list-btn {
            padding: 6px 12px;
            font-size: 0.75rem;
          }
          
          .toggle-btn {
            padding: 10px;
            font-size: 0.75rem;
          }
          
          .financial-year-selector {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
          
          .card {
            padding: 16px;
          }
          
          .card h3 {
            font-size: 0.85rem;
          }
          
          .amount-input {
            font-size: 1.2rem;
          }
          
          .currency-symbol {
            font-size: 1.2rem;
          }
        }
      `}</style>
    </div>
  );
}